import './TeamPage.scss';
import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { useLang } from '../../i18n';
import { useToast } from '../../components/Toast';
import { get, post } from '../../services/api';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Select from '../../components/Select';
import Avatar from '../../components/Avatar';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';
import Input from '../../components/Input';
import Skeleton from '../../components/Skeleton';
import EmptyState from '../../components/EmptyState';
import type { Paginated } from '../../types';

interface Organization {
  public_id: string;
  name: string;
}

interface Member {
  public_id: string;
  full_name: string;
  email: string;
  role: string;
  last_active_at?: string | null;
  can_manage?: boolean;
}

interface Invitation {
  public_id: string;
  contact: string;
  role: string;
  status: string;
}

const ROLE_OPTIONS = [
  { value: 'member', label: 'Member' },
  { value: 'manager', label: 'Manager' },
  { value: 'owner', label: 'Owner' },
];

export default function TeamPage() {
  const { t } = useLang();
  const toast = useToast();
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [orgId, setOrgId] = useState<string>('');
  const [members, setMembers] = useState<Member[]>([]);
  const [invites, setInvites] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [inviteContact, setInviteContact] = useState('');
  const [inviteRole, setInviteRole] = useState('member');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await get<Paginated<Organization>>('/organizations/');
        if (cancelled) return;
        const list = res.results || [];
        setOrgs(list);
        if (list[0]) setOrgId(list[0].public_id);
      } catch {
        if (!cancelled) setOrgs([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    (async () => {
      try {
        const [m, inv] = await Promise.all([
          get<Paginated<Member>>(`/organizations/${orgId}/members/`),
          get<Paginated<Invitation>>(`/organizations/${orgId}/invitations/`).catch(() => ({ count: 0, results: [], next: null, previous: null })),
        ]);
        if (cancelled) return;
        setMembers(m.results || []);
        setInvites(inv.results || []);
      } catch {
        if (!cancelled) { setMembers([]); setInvites([]); }
      }
    })();
    return () => { cancelled = true; };
  }, [orgId]);

  const sendInvite = async () => {
    if (!orgId || !inviteContact.trim()) return;
    try {
      await post(`/organizations/${orgId}/invite/`, { contact: inviteContact.trim(), role: inviteRole });
      toast.success(t('profile.saved'));
      setModal(false);
      setInviteContact('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    }
  };

  if (loading) return <Skeleton height={200} />;

  return (
    <div className="team-page">
      <header className="team-page__head">
        <h1 className="team-page__title">{t('team.title')}</h1>
        <Button variant="primary" size="md" iconLeft={<Plus size={14} />} onClick={() => setModal(true)}>
          {t('team.invite')}
        </Button>
      </header>
      {orgs.length > 1 ? (
        <Card padding="md">
          <Select
            label={t('team.org_selector')}
            value={orgId}
            onChange={setOrgId}
            options={orgs.map((o) => ({ value: o.public_id, label: o.name }))}
          />
        </Card>
      ) : null}

      <Card padding="md">
        <h2 className="team-page__section">{t('team.members')}</h2>
        {members.length === 0 ? (
          <EmptyState title="—" />
        ) : (
          <ul className="team-page__list">
            {members.map((m, i) => (
              <li key={m.public_id} className="team-page__item">
                {i > 0 ? <hr className="team-page__divider" /> : null}
                <div className="team-page__row">
                  <Avatar name={m.full_name || m.email} size="md" />
                  <div className="team-page__info">
                    <div className="team-page__name">{m.full_name || m.email}</div>
                    <div className="team-page__email">{m.email}</div>
                  </div>
                  <Badge variant="brand">{m.role}</Badge>
                  <div className="team-page__last">{m.last_active_at ? new Date(m.last_active_at).toLocaleDateString() : '—'}</div>
                  {m.can_manage ? (
                    <div className="team-page__actions">
                      <Button variant="ghost" size="sm">{t('team.role.change')}</Button>
                      <Button variant="ghost" size="sm">{t('team.role.remove')}</Button>
                    </div>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card padding="md">
        <h2 className="team-page__section">{t('team.invitations')}</h2>
        {invites.length === 0 ? (
          <EmptyState title="—" />
        ) : (
          <ul className="team-page__list">
            {invites.map((inv, i) => (
              <li key={inv.public_id} className="team-page__item">
                {i > 0 ? <hr className="team-page__divider" /> : null}
                <div className="team-page__row">
                  <div className="team-page__info">
                    <div className="team-page__name">{inv.contact}</div>
                    <div className="team-page__email">{inv.role}</div>
                  </div>
                  <Badge variant="warning">{inv.status}</Badge>
                  <Button variant="ghost" size="sm">{t('team.invite.revoke')}</Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Modal open={modal} onClose={() => setModal(false)} title={t('team.invite')}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-3)' }}>
          <Input label={t('team.invite.email')} value={inviteContact} onChange={(e) => setInviteContact(e.target.value)} />
          <Select label={t('team.invite.role')} value={inviteRole} onChange={setInviteRole} options={ROLE_OPTIONS} />
          <Button variant="primary" size="md" onClick={sendInvite}>{t('team.invite.send')}</Button>
        </div>
      </Modal>
    </div>
  );
}
